import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import config from '../config';

const API = config.apiUrl;

const ResultsPage = ({ assessment, user }) => {
  const navigate = useNavigate();
  const [detailedResults, setDetailedResults] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    loadDetailedResults();
  }, [assessment]);

  const loadDetailedResults = async () => {
    try {
      const response = await axios.get(`${API}/assessments/${assessment.id}`);
      setDetailedResults(response.data);
      setLoading(false);
    } catch (err) {
      console.error('Error loading detailed results:', err);
      setLoading(false);
    }
  };

  const handleExport = () => {
    // Générer un export HTML simple pour l'impression/PDF
    const printWindow = window.open('', '_blank');
    const content = generatePrintContent();
    printWindow.document.write(content);
    printWindow.document.close();
    printWindow.print();
  };

  const generatePrintContent = () => {
    if (!detailedResults) return '';

    const date = new Date(detailedResults.created_at).toLocaleDateString('fr-FR');
    const balance = detailedResults.balance;
    
    let html = `
      <!DOCTYPE html>
      <html>
      <head>
        <title>Résultats Stress Management Test - ${detailedResults.user_name}</title>
        <style>
          body { font-family: Arial, sans-serif; padding: 40px; color: #333; }
          h1 { color: #1e40af; border-bottom: 3px solid #1e40af; padding-bottom: 10px; }
          h2 { color: #475569; margin-top: 30px; }
          .header { margin-bottom: 30px; }
          .info { background: #f1f5f9; padding: 15px; border-radius: 8px; margin-bottom: 20px; }
          .balance { background: #eff6ff; padding: 20px; border-radius: 8px; margin-bottom: 30px; border: 2px solid #3b82f6; }
          .balance-grid { display: grid; grid-template-columns: 1fr 1fr; gap: 20px; margin-top: 15px; }
          .balance-card { background: white; padding: 15px; border-radius: 8px; border: 1px solid #e2e8f0; }
          table { width: 100%; border-collapse: collapse; margin-top: 20px; }
          th, td { padding: 12px; text-align: left; border-bottom: 1px solid #e2e8f0; }
          th { background: #1e40af; color: white; }
          tr:hover { background: #f8fafc; }
          .percentage { font-weight: bold; font-size: 18px; }
          .badge { display: inline-block; padding: 2px 8px; border-radius: 4px; font-size: 11px; font-weight: bold; }
          .badge-good { background: #dcfce7; color: #166534; }
          .badge-bad { background: #fee2e2; color: #991b1b; }
          .high { color: #059669; }
          .medium { color: #d97706; }
          .low { color: #dc2626; }
          .footer { margin-top: 40px; font-size: 12px; color: #64748b; border-top: 1px solid #e2e8f0; padding-top: 20px; }
        </style>
      </head>
      <body>
        <div class="header">
          <h1>Résultats du Questionnaire Stress Management Test</h1>
          <div class="info">
            <p><strong>Répondant :</strong> ${detailedResults.user_name}</p>
            <p><strong>Date :</strong> ${date}</p>
            <p><strong>ID Évaluation :</strong> ${detailedResults.id}</p>
          </div>
        </div>
    `;

    // Ajouter le bilan si disponible
    if (balance) {
      html += `
        <div class="balance">
          <h2 style="margin-top: 0;">📊 Bilan Global - Levier des Mécanismes</h2>
          <div style="text-align: center; margin: 20px 0;">
            <div style="font-size: 48px; font-weight: bold; color: #1e40af;">
              ${balance.summary.positive_percentage}%
            </div>
            <div style="font-size: 18px; margin: 10px 0;">
              <strong>${balance.summary.global_assessment}</strong>
            </div>
            <div style="color: #64748b;">
              ${balance.summary.total_positive_points} points positifs / ${balance.summary.total_mechanisms} mécanismes
            </div>
            <div style="margin-top: 15px; font-style: italic;">
              ${balance.summary.global_message}
            </div>
          </div>
          
          <div class="balance-grid">
            <div class="balance-card">
              <strong style="color: #059669;">✅ Bons Mécanismes (10 total)</strong>
              <ul style="margin-top: 10px; padding-left: 20px;">
                <li>≥ 50% (points positifs) : <strong>${balance.good_mechanisms.positive_count}</strong></li>
                <li>< 50% (à développer) : <strong>${balance.good_mechanisms.negative_count}</strong></li>
              </ul>
            </div>
            
            <div class="balance-card">
              <strong style="color: #dc2626;">⚠️ Mauvais Mécanismes (4 total)</strong>
              <ul style="margin-top: 10px; padding-left: 20px;">
                <li>< 50% (points positifs) : <strong>${balance.bad_mechanisms.positive_count}</strong></li>
                <li>≥ 50% (vigilance) : <strong>${balance.bad_mechanisms.negative_count}</strong></li>
              </ul>
            </div>
          </div>
        </div>
      `;
    }

    html += `
        <h2>Scores Détaillés des Mécanismes d'Adaptation</h2>
        <table>
          <thead>
            <tr>
              <th>Mécanisme</th>
              <th>Score</th>
              <th>%</th>
              <th>Interprétation</th>
            </tr>
          </thead>
          <tbody>
    `;

    const goodMechanisms = [
      "Coping actif", "Planification", "Soutien Instrumental", "Soutien Émotionnel",
      "Ventilation", "Recadrage Positif", "Acceptation", "Humour", "Religion", "Distraction"
    ];

    Object.entries(detailedResults.scores).forEach(([mechanism, data]) => {
      const percentageClass = data.percentage >= 60 ? 'high' : data.percentage >= 40 ? 'medium' : 'low';
      
      html += `
        <tr>
          <td><strong>${mechanism}</strong></td>
          <td>${data.score} / ${data.max_score}</td>
          <td class="percentage ${percentageClass}">${data.percentage}%</td>
          <td>${data.interpretation}</td>
        </tr>
      `;
    });

    html += `
          </tbody>
        </table>

        <div class="footer">
          <p>Ce rapport a été généré automatiquement par le système Stress Management Test.</p>
          <p>Les résultats reflètent vos stratégies d'adaptation telles que rapportées au moment de l'évaluation.</p>
        </div>
      </body>
      </html>
    `;

    return html;
  };

  const getCategoryColor = (percentage) => {
    if (percentage >= 75) return 'text-green-600 bg-green-50 border-green-200';
    if (percentage >= 50) return 'text-blue-600 bg-blue-50 border-blue-200';
    if (percentage >= 25) return 'text-orange-600 bg-orange-50 border-orange-200';
    return 'text-slate-600 bg-slate-50 border-slate-200';
  };

  const getBalanceColor = (percentage) => {
    if (percentage >= 80) return 'text-green-600 bg-green-100';
    if (percentage >= 60) return 'text-blue-600 bg-blue-100';
    if (percentage >= 40) return 'text-orange-600 bg-orange-100';
    return 'text-red-600 bg-red-100';
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mx-auto"></div>
          <p className="mt-4 text-slate-600">Chargement des résultats...</p>
        </div>
      </div>
    );
  }

  if (!detailedResults) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="text-center">
          <p className="text-red-600">Erreur lors du chargement des résultats.</p>
          <button 
            onClick={() => navigate('/')}
            className="mt-4 text-blue-600 hover:underline"
          >
            Retour à l'accueil
          </button>
        </div>
      </div>
    );
  }

  const date = new Date(detailedResults.created_at).toLocaleDateString('fr-FR', {
    year: 'numeric',
    month: 'long',
    day: 'numeric',
    hour: '2-digit',
    minute: '2-digit'
  });

  return (
    <div className="min-h-screen py-8 px-4" data-testid="results-page">
      <div className="max-w-6xl mx-auto">
        {/* Header */}
        <div className="bg-white rounded-xl shadow-lg p-8 mb-6">
          <div className="flex items-center justify-between flex-wrap gap-4">
            <div>
              <h1 className="text-3xl font-bold text-slate-800 mb-2">Résultats de votre évaluation</h1>
              <p className="text-slate-600">
                {detailedResults.user_name} - {date}
              </p>
            </div>
            <div className="flex gap-3 flex-wrap">
              <button
                onClick={() => navigate('/explications')}
                className="bg-purple-600 hover:bg-purple-700 text-white font-semibold py-3 px-6 rounded-lg transition flex items-center gap-2"
                data-testid="explications-btn"
              >
                <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                </svg>
                Explications
              </button>
              <button
                onClick={handleExport}
                className="bg-green-600 hover:bg-green-700 text-white font-semibold py-3 px-6 rounded-lg transition flex items-center gap-2"
                data-testid="export-pdf-btn"
              >
                <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 10v6m0 0l-3-3m3 3l3-3m2 8H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                </svg>
                Exporter (PDF)
              </button>
              <button
                onClick={() => navigate('/history')}
                className="bg-blue-600 hover:bg-blue-700 text-white font-semibold py-3 px-6 rounded-lg transition"
                data-testid="view-history-btn"
              >
                Voir l'historique
              </button>
            </div>
          </div>
        </div>

        {/* Summary Cards */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-6">
          <div className="bg-white rounded-xl shadow-md p-6">
            <div className="flex items-center gap-3">
              <div className="w-12 h-12 bg-blue-100 rounded-full flex items-center justify-center">
                <svg className="w-6 h-6 text-blue-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
                </svg>
              </div>
              <div>
                <div className="text-2xl font-bold text-slate-800">14</div>
                <div className="text-sm text-slate-600">Mécanismes évalués</div>
              </div>
            </div>
          </div>

          <div className="bg-white rounded-xl shadow-md p-6">
            <div className="flex items-center gap-3">
              <div className="w-12 h-12 bg-green-100 rounded-full flex items-center justify-center">
                <svg className="w-6 h-6 text-green-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 7h8m0 0v8m0-8l-8 8-4-4-6 6" />
                </svg>
              </div>
              <div>
                <div className="text-2xl font-bold text-slate-800">28</div>
                <div className="text-sm text-slate-600">Questions répondues</div>
              </div>
            </div>
          </div>

          <div className="bg-white rounded-xl shadow-md p-6">
            <div className="flex items-center gap-3">
              <div className="w-12 h-12 bg-purple-100 rounded-full flex items-center justify-center">
                <svg className="w-6 h-6 text-purple-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 19v-6a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2a2 2 0 002-2zm0 0V9a2 2 0 012-2h2a2 2 0 012 2v10m-6 0a2 2 0 002 2h2a2 2 0 002-2m0 0V5a2 2 0 012-2h2a2 2 0 012 2v14a2 2 0 01-2 2h-2a2 2 0 01-2-2z" />
                </svg>
              </div>
              <div>
                <div className="text-2xl font-bold text-slate-800">
                  {Math.round(Object.values(detailedResults.scores).reduce((sum, m) => sum + m.percentage, 0) / 14)}%
                </div>
                <div className="text-sm text-slate-600">Score moyen</div>
              </div>
            </div>
          </div>
        </div>

        {/* Balance/Levier Section */}
        {detailedResults.balance && (
          <div className="bg-gradient-to-r from-blue-50 to-indigo-50 rounded-xl shadow-lg p-8 mb-6 border-2 border-blue-200">
            <h2 className="text-2xl font-bold text-slate-800 mb-6 flex items-center gap-3">
              <svg className="w-8 h-8 text-blue-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 19v-6a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2a2 2 0 002-2zm0 0V9a2 2 0 012-2h2a2 2 0 012 2v10m-6 0a2 2 0 002 2h2a2 2 0 002-2m0 0V5a2 2 0 012-2h2a2 2 0 012 2v14a2 2 0 01-2 2h-2a2 2 0 01-2-2z" />
              </svg>
              Bilan Global - Levier des Mécanismes
            </h2>

            {/* Global Assessment */}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-6">
              <div className={`rounded-xl p-6 ${getBalanceColor(detailedResults.balance.summary.positive_percentage)}`}>
                <div className="text-center">
                  <div className="text-6xl font-bold mb-2">
                    {detailedResults.balance.summary.positive_percentage}%
                  </div>
                  <div className="text-lg font-semibold">Points Positifs</div>
                  <div className="text-sm mt-2">
                    {detailedResults.balance.summary.total_positive_points} / {detailedResults.balance.summary.total_mechanisms} mécanismes
                  </div>
                </div>
              </div>

              <div className="bg-white rounded-xl p-6 border-2 border-slate-200">
                <div className="text-center">
                  <div className="text-3xl font-bold text-slate-800 mb-2">
                    {detailedResults.balance.summary.global_assessment}
                  </div>
                  <div className="text-sm text-slate-600 mt-3">
                    {detailedResults.balance.summary.global_message}
                  </div>
                </div>
              </div>
            </div>

            {/* Good Mechanisms Summary */}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-4">
              <div className="bg-white rounded-xl p-6 border-2 border-green-200">
                <h3 className="font-bold text-green-800 mb-3 flex items-center gap-2">
                  <span className="text-2xl">✅</span>
                  Bons Mécanismes (10 total)
                </h3>
                <div className="space-y-2">
                  <div className="flex justify-between items-center">
                    <span className="text-green-700">≥ 50% (points positifs)</span>
                    <span className="font-bold text-green-800 text-xl">
                      {detailedResults.balance.good_mechanisms.positive_count}
                    </span>
                  </div>
                  <div className="flex justify-between items-center">
                    <span className="text-orange-700">{'<'} 50% (à développer)</span>
                    <span className="font-bold text-orange-800 text-xl">
                      {detailedResults.balance.good_mechanisms.negative_count}
                    </span>
                  </div>
                </div>
              </div>

              <div className="bg-white rounded-xl p-6 border-2 border-red-200">
                <h3 className="font-bold text-red-800 mb-3 flex items-center gap-2">
                  <span className="text-2xl">⚠️</span>
                  Mauvais Mécanismes (4 total)
                </h3>
                <div className="space-y-2">
                  <div className="flex justify-between items-center">
                    <span className="text-green-700">{'<'} 50% (points positifs)</span>
                    <span className="font-bold text-green-800 text-xl">
                      {detailedResults.balance.bad_mechanisms.positive_count}
                    </span>
                  </div>
                  <div className="flex justify-between items-center">
                    <span className="text-red-700">≥ 50% (vigilance)</span>
                    <span className="font-bold text-red-800 text-xl">
                      {detailedResults.balance.bad_mechanisms.negative_count}
                    </span>
                  </div>
                </div>
              </div>
            </div>

            {/* Detailed Messages */}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mt-6">
              <div>
                <h4 className="font-semibold text-slate-700 mb-2">Détail des bons mécanismes :</h4>
                <div className="space-y-1">
                  {detailedResults.balance.good_mechanisms.details.map((detail, idx) => (
                    <div 
                      key={idx} 
                      className={`text-sm p-2 rounded ${detail.status === 'positive' ? 'bg-green-50' : 'bg-orange-50'}`}
                    >
                      {detail.message}
                    </div>
                  ))}
                </div>
              </div>

              <div>
                <h4 className="font-semibold text-slate-700 mb-2">Détail des mauvais mécanismes :</h4>
                <div className="space-y-1">
                  {detailedResults.balance.bad_mechanisms.details.map((detail, idx) => (
                    <div 
                      key={idx} 
                      className={`text-sm p-2 rounded ${detail.status === 'positive' ? 'bg-green-50' : 'bg-red-50'}`}
                    >
                      {detail.message}
                    </div>
                  ))}
                </div>
              </div>
            </div>
          </div>
        )}

        {/* Detailed Results */}
        <div className="bg-white rounded-xl shadow-lg p-8">
          <h2 className="text-2xl font-bold text-slate-800 mb-6">Détail des mécanismes d'adaptation</h2>
          
          <div className="space-y-6">
            {Object.entries(detailedResults.scores).map(([mechanism, data]) => (
              <div key={mechanism} className="border-b border-slate-200 pb-6 last:border-b-0" data-testid={`mechanism-${mechanism}`}>
                <div className="flex items-start justify-between mb-3">
                  <div className="flex-1">
                    <h3 className="text-lg font-semibold text-slate-800 mb-2">{mechanism}</h3>
                    <p className="text-sm text-slate-600 mt-1">{data.interpretation}</p>
                  </div>
                  <div className="ml-4 text-right">
                    <div className={`inline-block px-4 py-2 rounded-lg border-2 ${getCategoryColor(data.percentage)}`}>
                      <div className="text-2xl font-bold">{data.percentage}%</div>
                      <div className="text-xs">{data.score}/{data.max_score}</div>
                    </div>
                  </div>
                </div>
              
                {/* Progress bar */}
                <div className="w-full bg-slate-200 rounded-full h-3">
                  <div 
                    className={`h-3 rounded-full transition-all duration-500 ${
                      data.percentage >= 75 ? 'bg-green-500' :
                      data.percentage >= 50 ? 'bg-blue-500' :
                      data.percentage >= 25 ? 'bg-orange-500' :
                      'bg-slate-400'
                    }`}
                    style={{ width: `${data.percentage}%` }}
                  ></div>
                </div>
              </div>
            ))}
          </div>
        </div>

        {/* Legend */}
        <div className="bg-slate-50 rounded-xl p-6 mt-6">
          <h3 className="font-semibold text-slate-800 mb-3">Légende des scores</h3>
          <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
            <div className="flex items-center gap-2">
              <div className="w-4 h-4 bg-green-500 rounded"></div>
              <span className="text-sm text-slate-700">75-100% : Utilisation élevée</span>
            </div>
            <div className="flex items-center gap-2">
              <div className="w-4 h-4 bg-blue-500 rounded"></div>
              <span className="text-sm text-slate-700">50-74% : Utilisation modérée</span>
            </div>
            <div className="flex items-center gap-2">
              <div className="w-4 h-4 bg-orange-500 rounded"></div>
              <span className="text-sm text-slate-700">25-49% : Utilisation faible</span>
            </div>
            <div className="flex items-center gap-2">
              <div className="w-4 h-4 bg-slate-400 rounded"></div>
              <span className="text-sm text-slate-700">0-24% : Utilisation très faible</span>
            </div>
          </div>
        </div>

        {/* Action buttons */}
        <div className="mt-6 flex gap-4 justify-center">
          <button
            onClick={() => navigate('/')}
            className="bg-slate-600 hover:bg-slate-700 text-white font-semibold py-3 px-8 rounded-lg transition"
            data-testid="new-assessment-btn"
          >
            Nouvelle évaluation
          </button>
        </div>
      </div>
    </div>
  );
};

export default ResultsPage;
